import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'
import { DEFAULT_MULTIPACK } from '../constants.js'
import {
  ToasterService,
  SbLocaleService,
  ShopifyCartService,
  ShopifyProductService,
} from '../services'

import type { ShopifyProductVariant, Multipack } from '../types/index.js'

interface Scope {
  // OPTIONS
  /** The current selected quantity of the product */
  quantity: number
  /** The multiplicator for the quantity (used for multipacks) */
  multiplicator: number
  /** Currently selected product variant */
  variant?: ShopifyProductVariant
  /** Available multipack options with count and discount */
  multipacks: Multipack[]

  // METHODS
  decrease: SbProductQuantityComponent['decrease']
  increase: SbProductQuantityComponent['increase']
  onSelectMultipackCount: SbProductQuantityComponent['onSelectMultipackCount']

  // TEMPLATES
}

/**
 * Component to handle product quantity selection with multipack support.
 * Allows customers to select product quantities while respecting inventory limits
 * and multipack discounts.
 * @emits multipack-selected - Fired when the multipack option is changed
 */
export class SbProductQuantityComponent extends Component {
  public static tagName = 'sb-product-quantity'

  protected localeService = SbLocaleService.getSingleton()
  protected toasterService = ToasterService.getSingleton()

  /**
   * The component's scope containing all reactive properties and methods
   */
  public scope: Scope = {
    // OPTIONS
    quantity: 1,
    multiplicator: 1,
    multipacks: [DEFAULT_MULTIPACK],

    // METHODS
    decrease: this.decrease,
    increase: this.increase,
    onSelectMultipackCount: this.onSelectMultipackCount,
    // TEMPLATES
  }

  /**
   * Gets the currently active multipack option
   * @returns The active multipack or undefined if none is active
   */
  public get activeMultipack(): Multipack | undefined {
    return this.scope.multipacks.find(multipack => multipack.active)
  }

  public get activeMultipackQuantity(): number {
    return this.activeMultipack?.quantity || 1
  }

  /**
   * Resets the quantity multiplicator to 1 and triggers related updates
   */
  public resetMultiplicator() {
    this.scope.multiplicator = 1
    this.onMultiplicatorChanged()
  }

  /**
   * Disables all multipack options
   */
  public disableMultipacks(): void {
    this.scope.multipacks.forEach(multipack => {
      multipack.active = false
    })
  }

  /**
   * Resets multipacks to initial state with first option active
   */
  public resetMultipacks(): void {
    // Already active
    if (this.scope.multipacks[0]!.active) {
      return
    }
    this.disableMultipacks()
    this.scope.multipacks[0]!.active = true
    this.recalculateQuantity()
  }

  /**
   * List of attributes that trigger updates when changed
   */
  static get observedAttributes(): string[] {
    return [
      'quantity',
      'multiplicator',
      'variant',
      'multipacks',
      'already-discounted',
    ]
  }

  /**
   * List of required attributes for this component
   */
  protected requiredAttributes(): string[] {
    return []
  }

  constructor() {
    super()
  }

  protected connectedCallback(): void {
    super.connectedCallback()
    this.init(SbProductQuantityComponent.observedAttributes)
  }

  protected disconnectedCallback(): void {
    super.disconnectedCallback()
  }

  async beforeBind(): Promise<void> {
    await super.beforeBind()
  }

  async afterBind(): Promise<void> {
    await super.afterBind()
    this.classList.add(`${SbProductQuantityComponent.tagName}-ready`)
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ): void {
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    switch (attributeName) {
      case 'quantity':
        if (newValue !== oldValue) {
          this.recalculateQuantity()
        }
        break
      case 'multiplicator':
        if (newValue !== oldValue) {
          this.onMultiplicatorChanged()
        }
        break
      case 'multipacks':
        if (newValue !== oldValue) {
          this.onMultipackChanged()
        }
        break
      case 'variant':
        if (newValue !== oldValue) {
          this.onVariantChanged()
        }
        break
    }
  }

  /**
   * Dispatches a custom event when multipack selection changes
   */
  protected triggerMultipackSelectedEvent(): void {
    const event = new CustomEvent('multipack-selected', {
      detail: {
        variant: this.scope.variant,
      },
    })
    this.dispatchEvent(event)
  }

  /**
   * Gets the current cart quantity for the selected variant
   */
  protected getVariantCartQuantity(): number {
    return ShopifyCartService.getTotalVariantQuantityInCart(
      this.scope.variant?.id || 0,
    )
  }

  /**
   * Updates the multiplicator by the given change amount
   * @param change - The amount to change the multiplicator by (positive or negative)
   */
  protected async updateMultiplicator(change: number): Promise<void> {
    if (!this.scope.variant) {
      console.warn(
        '[sb-product-quantity.updateMultiplicator] No variant selected',
      )
      return
    }

    const { increment } = ShopifyProductService.getQuantityRule(
      this.scope.variant,
    )
    const nextMultiplicator = this.scope.multiplicator + change * increment

    // Validate the new multiplicator value
    const validateQuantityResult = ShopifyCartService.validateQuantity(
      this.scope.variant,
      nextMultiplicator,
      {
        multipackQuantity: this.activeMultipackQuantity,
      },
    )

    // Only show error if the validated multiplicator is less than the requested one
    if (!validateQuantityResult.isValid) {
      await ShopifyProductService.showQuantityValidationError(
        validateQuantityResult,
      )
    }

    this.scope.multiplicator = validateQuantityResult.allowedUserInput
    this.recalculateQuantity()
  }

  /**
   * Increase the multiplicator if inventory allows
   */
  public async increase(): Promise<void> {
    await this.updateMultiplicator(1)
  }

  /**
   * Decrease the multiplicator if above minimum
   */
  public async decrease(): Promise<void> {
    await this.updateMultiplicator(-1)
  }

  public onSelectMultipackCount(multipack: Multipack): void {
    if (multipack.active) {
      return
    }
    this.disableMultipacks()
    multipack.active = true
    this.recalculateQuantity()
    this.triggerMultipackSelectedEvent()
  }

  /**
   * Handles changes to the multiplicator value
   * Validates the new value and recalculates quantities
   */
  protected onMultiplicatorChanged(): void {
    this.recalculateQuantity()
  }

  /**
   * Handles changes to multipack selection
   * Updates labels and resets multiplicator if needed
   */
  protected onMultipackChanged(): void {
    this.translateMulipacks()
    this.resetMultiplicator()
  }

  /**
   * Handles changes to the selected variant
   * Resets quantity and multipack selection
   */
  protected async onVariantChanged(): Promise<void> {
    this.resetMultipacks()
  }

  /**
   * Recalculates the total quantity based on multiplicator and active multipack
   */
  protected recalculateQuantity(): void {
    if (!this.scope.variant) {
      console.warn(
        '[sb-product-quantity.recalculateQuantity] No variant selected',
      )
      return
    }

    // Get validated quantity without updating state yet
    const { allowedUserInput } = ShopifyCartService.validateQuantity(
      this.scope.variant,
      this.scope.multiplicator,
      {
        multipackQuantity: this.activeMultipack?.quantity,
      },
    )

    // Only update if values actually changed to prevent recursion
    if (this.scope.multiplicator !== allowedUserInput) {
      this.scope.multiplicator = allowedUserInput
    }

    const newQuantity = allowedUserInput * this.activeMultipackQuantity
    if (this.scope.quantity !== newQuantity) {
      this.scope.quantity = newQuantity
    }
  }

  /**
   * Translates multipack labels based on current language
   * Updates labels for quantity and discount information
   */
  protected async translateMulipacks(): Promise<void> {
    for (const multipack of this.scope.multipacks) {
      const placeholders = {
        count: multipack.quantity.toString(),
        percent: multipack.percentage.toString(),
      }
      multipack.label ||= await this.localeService._(
        'products.product.option_name.pack_number.' +
          multipack.quantity.toString() +
          '_html',
        placeholders,
      )
      // If there is no label for the specific count, use the default one
      if (!multipack.label) {
        multipack.label = await this.localeService._(
          'products.product.option_name.pack_number.default_html',
          placeholders,
        )
      }
      if (multipack.percentage > 0) {
        multipack.discountLabel ||= await this.localeService._(
          'products.product.option_name.discount_html',
          placeholders,
        )
      } else {
        multipack.discountLabel ||= ''
      }
    }
  }

  /**
   * Returns the component's template
   * @returns {ReturnType<TemplateFunction>} The template or null if using child HTML
   */
  protected template(): ReturnType<TemplateFunction> {
    // Use this template if this custom element has no child html
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component has not it's own template, you need to add the child
          HTML in the template
        </p>
      )
    }
    return null
  }
}
